import { sortBy } from 'lodash';
import { Difference } from 'microdiff';
import { ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetCompanyOffices } from '~/data/companyOffices';
import { Enum, EnumListKey, useAllEnums } from '~/data/enums';
import { useLanguages } from '~/data/languages';
import { useAllOrgUnits } from '~/data/orgunits';
import { useRoleTranslations } from '~/data/user';
import {
  AuditOperation,
  AuditSummaryData_EntityData,
  AuditTargetType,
  AuditType,
} from '~/data/userActivity/useGetAuditSummary';
import { UserActivityChangeEventsRow } from '~/scenes/PeopleModule/components/EmployeeActions/EmployeeActivityLogDialog/components/EmployeeActivityLog/EmployeeChangeActivityLog/data/index';
import {
  browserLogError,
  convertToDateOrUndefined,
  getEnumName,
  getFormattedDateString,
} from '~/utils/helper';

type EnumWithOptions = Enum & {
  options: Enum[];
};

export const useActivityLogDataTranslations = () => {
  const { t } = useTranslation();

  const getTranslationOrValue = useCallback(
    <T>(value: T | string, translationKeys: [string, string][]) => {
      const tKey = translationKeys.find(([key]) => key === value);

      if (!tKey) {
        browserLogError(
          `User Activity Log - Changes: Translation key for "${value}" not found.`,
        );
      }

      return !!tKey ? tKey[1] : value;
    },
    [],
  );

  const getAuditTypeTranslation = useCallback(
    (row: UserActivityChangeEventsRow) => {
      const { targetType, targetSubType, auditType } = row;

      const auditTypeTranslationMapping: { [key in AuditType]: string } = {
        [AuditType.AbsenceLong]: t('laLongAbsence'),
        [AuditType.AbsenceShort]: t('laShortAbsence'),
        [AuditType.AuthUser]: t('userAuthentication'),
        [AuditType.AuthUserRole]: t('employeeRoles'),
        [AuditType.AuthResponsibility]: t('employeeResponsibility'),
        [AuditType.CustomData]: t('laCustomData'),
        [AuditType.EmployeeContactHome]: t('laHomeContact'),
        [AuditType.EmployeeContactWork]: t('laWorkContact'),
        [AuditType.EmployeeContactOther]: t('laOtherContact'),
        [AuditType.EmployeeHomeContact]: `${t('laHomeContact')} - ${t(
          'laContact',
        )}`,
        [AuditType.EmployeeHomeAddress]: `${t('laHomeContact')} - ${t(
          'laAddress',
        )}`,
        [AuditType.EmployeeHomeEmail]: `${t('laHomeContact')} - ${t(
          'laEmail',
        )}`,
        [AuditType.EmployeeHomePhone]: `${t('laHomeContact')} - ${t(
          'laPhone',
        )}`,
        [AuditType.EmployeeWorkContact]: `${t('laWorkContact')} - ${t(
          'laContact',
        )}`,
        [AuditType.EmployeeWorkEmail]: `${t('laWorkContact')} - ${t(
          'laEmail',
        )}`,
        [AuditType.EmployeeWorkPhone]: `${t('laWorkContact')} - ${t(
          'laPhone',
        )}`,
        [AuditType.EmployeeWorkMobile]: `${t('laWorkContact')} - ${t(
          'laWorkMobile',
        )}`,
        [AuditType.EmployeeOtherContact]: `${t('laOtherContact')} - ${t(
          'laContact',
        )}`,
        [AuditType.EmployeeOtherAddress]: `${t('laOtherContact')} - ${t(
          'laAddress',
        )}`,
        [AuditType.EmployeeOtherEmail]: `${t('laOtherContact')} - ${t(
          'laEmail',
        )}`,
        [AuditType.EmployeeOtherPhone]: `${t('laOtherContact')} - ${t(
          'laPhone',
        )}`,
        [AuditType.Employment]: t('laEmployment'),
        [AuditType.EmploymentDetail]: t('laEmplDetail'),
        [AuditType.PersonProfile]: t('laPersonalData'),
        [AuditType.EmploymentPosition]: t('laEmployment'),
      };

      // TODO key list is incomplete
      const customDataTypeTranslationKey: { [p: string]: string } = {
        EMPLOYEE: t('laCustomData'),
        EMPLOYEE_POS: t('laPositionCustomData'),
        EMPLOYMENT_DETAILS: `${t('laEmplDetail')} ${t('laCustomData')}`,
      };

      switch (targetType) {
        case AuditTargetType.Employee: {
          switch (auditType) {
            case AuditType.EmploymentPosition:
            case AuditType.EmploymentDetail:
              return `${t('laSchedule')} - ${t(
                auditTypeTranslationMapping[auditType],
              )}`;
            default: {
              return getTranslationOrValue(
                auditType,
                Object.entries(auditTypeTranslationMapping),
              );
            }
          }
        }
        case AuditTargetType.Entity: {
          const customDataType = targetSubType ?? '';

          switch (auditType) {
            case AuditType.CustomData: {
              return (
                customDataTypeTranslationKey[customDataType] ?? customDataType
              );
            }
            default: {
              return customDataType;
            }
          }
        }
        default:
          return '';
      }
    },
    [t, getTranslationOrValue],
  );

  const getAuditTargetTypeTranslation = useCallback(
    (row: UserActivityChangeEventsRow) => {
      const { targetType } = row;

      const auditTargetTypeTranslationKey: {
        [key in AuditTargetType]: string;
      } = {
        [AuditTargetType.Employee]: t('laEmployee'),
        [AuditTargetType.Entity]: 'entity',
      };

      switch (targetType) {
        case AuditTargetType.Entity: {
          // Entity event is Employee related or not
          return ['EMPLOYEE', 'EMPLOYEE_POS', 'EMPLOYMENT_DETAILS'].includes(
            row.targetSubType ?? '',
          )
            ? `${t('laEmployee')} - ${t('laCustomData')}`
            : t('auditTargetEntity');
        }
        default:
          return getTranslationOrValue(
            targetType,
            Object.entries(auditTargetTypeTranslationKey),
          );
      }
    },
    [t, getTranslationOrValue],
  );

  const getAuditOperationTranslation = useCallback(
    (operation: AuditOperation) => {
      const auditOperationTranslations: {
        [key in AuditOperation]: string;
      } = {
        [AuditOperation.Insert]: t('insert'),
        [AuditOperation.Update]: t('update'),
        [AuditOperation.Delete]: t('delete'),
      };

      return getTranslationOrValue(
        operation,
        Object.entries(auditOperationTranslations),
      );
    },
    [t, getTranslationOrValue],
  );

  return {
    getAuditTypeTranslation,
    getAuditTargetTypeTranslation,
    getAuditOperationTranslation,
  };
};

export type UserActivityLogEventDataPoint = {
  key: string;
  label: string;
  value?: string | number | ReactNode | Date;
  originalValue?: string | number;
  differenceType?: Difference['type'];
};

export const useActivityLogEventDataTranslation = () => {
  const { t } = useTranslation();

  const { getUnitByIdIncludeDeleted } = useAllOrgUnits();
  const { getRoleTranslation } = useRoleTranslations();
  const { getLanguageByCode } = useLanguages();
  const { getCustomDataEnums, getEnums } = useAllEnums();
  const { getOfficeById } = useGetCompanyOffices();

  const enumsData = useMemo(() => {
    const getCustomDataListKey = (listKey: EnumListKey, fieldCode: string) => {
      return `${listKey.replace(
        'CUSTOM_ENTITY_',
        '__CUSTOMDATA__',
      )}__${fieldCode}`;
    };

    const getCustomDataEnumsWithOptions = (listKey: EnumListKey) => {
      return getEnums(listKey).map((e): EnumWithOptions => {
        return {
          ...e,
          options: getCustomDataEnums(getCustomDataListKey(listKey, e.code)),
        };
      });
    };

    return {
      countryEnums: getEnums(EnumListKey.COUNTRY),
      absenceEnums: {
        shortReason: getEnums(EnumListKey.ABSENCE_SHORT_REASON),
        longReason: getEnums(EnumListKey.ABSENCE_LONG_REASON),
        status: getEnums(EnumListKey.ABSENCE_STATUS),
      },
      customDataEnums: {
        EMPLOYEE: getCustomDataEnumsWithOptions(
          EnumListKey.CUSTOM_ENTITY_EMPLOYEE,
        ),
        EMPLOYEE_POS: getCustomDataEnumsWithOptions(
          EnumListKey.CUSTOM_ENTITY_EMPLOYEE_POS,
        ),
      },
    };
  }, [getEnums, getCustomDataEnums]);

  const getTranslatedEventData = useCallback(
    (row: UserActivityChangeEventsRow): UserActivityLogEventDataPoint[] => {
      const { targetType, auditType, data } = row;

      const { absenceEnums, customDataEnums, countryEnums } = enumsData;

      const getDefaultDataPoints = (
        data: UserActivityChangeEventsRow['data'],
      ): UserActivityLogEventDataPoint[] => {
        return (
          Object.entries(data).map(
            ([key, value]): UserActivityLogEventDataPoint => {
              return {
                key,
                label: key,
                value,
                originalValue: value,
              };
            },
          ) ?? []
        );
      };

      const filterDataPointByAllowedKeys =
        (allowedKeys: string[]) => (dp: UserActivityLogEventDataPoint) => {
          return allowedKeys.includes(dp.key);
        };

      const filterDataPointByDeniedKeys =
        (deniedKeys: string[]) => (dp: UserActivityLogEventDataPoint) => {
          return !deniedKeys.includes(dp.key);
        };

      const getTranslatedDataPointLabel =
        (keyLabelMap: { [p: string]: string }) =>
        (dp: UserActivityLogEventDataPoint): UserActivityLogEventDataPoint => {
          return {
            ...dp,
            label: keyLabelMap[dp.key] ?? dp.key,
          };
        };

      const getDataPointsByKeyLabelMap = (keyLabelMap: {
        [p: string]: string;
      }) => {
        const keys = Object.keys(keyLabelMap);

        return sortBy(
          getDefaultDataPoints(data)
            .filter(filterDataPointByAllowedKeys(keys))
            .map(getTranslatedDataPointLabel(keyLabelMap)),
          (dp) => keys.indexOf(dp.key),
        );
      };

      switch (targetType) {
        case AuditTargetType.Entity: {
          const { entity_fk, attribute_name_fk, attribute_value } =
            data as AuditSummaryData_EntityData;

          const customDataFields =
            Object.entries(customDataEnums).find(
              ([key]) => key === entity_fk,
            )?.[1] ?? [];
          const field = customDataFields.find(
            ({ code }) => code === attribute_name_fk,
          );
          const label = field?.name ?? attribute_name_fk;
          const fieldOptions = !!field?.options ? field.options : undefined;
          const value =
            fieldOptions?.find((o) => o.code === attribute_value)?.name ??
            attribute_value;

          return [
            {
              key: attribute_name_fk,
              label,
              value,
            },
          ];
        }
        case AuditTargetType.Employee: {
          switch (auditType) {
            case AuditType.AuthUser: {
              const keyLabelMap = {
                language_fk: t('laLanguage'),
                username: t('username'),
                current_successful_login_ip: t('ipAddress'),
                current_successful_login_ts: t('laTime'),
              };

              return getDefaultDataPoints(data)
                .filter(filterDataPointByAllowedKeys(Object.keys(keyLabelMap)))
                .map(getTranslatedDataPointLabel(keyLabelMap))
                .map((dp) => {
                  switch (dp.key) {
                    case 'language_fk': {
                      const language_fk = dp.originalValue as string;

                      return {
                        ...dp,
                        value:
                          getLanguageByCode(language_fk)?.fComLanguageName ??
                          language_fk,
                      };
                    }
                    default:
                      return dp;
                  }
                });
            }
            case AuditType.AuthUserRole: {
              return getDefaultDataPoints(data).map(
                (dp): UserActivityLogEventDataPoint => {
                  switch (dp.key) {
                    case 'role_name': {
                      return {
                        ...dp,
                        label: t('laRole'),
                        value: getRoleTranslation(dp.value as string),
                      };
                    }
                    default:
                      return dp;
                  }
                },
              );
            }
            case AuditType.AuthResponsibility: {
              return getDefaultDataPoints(data)
                .filter(filterDataPointByAllowedKeys(['unit_fk', 'role_name']))
                .map((dp) => {
                  switch (dp.key) {
                    case 'unit_fk': {
                      const unit_fk = dp.originalValue as number | undefined;

                      const unit = getUnitByIdIncludeDeleted(unit_fk);
                      const unitName = !!unit
                        ? `${unit?.fOrgUnitName} (${unit?.fOrgUnitNumber})`
                        : unit_fk;

                      return {
                        ...dp,
                        label: t('laUnit'),
                        value: unitName,
                      };
                    }
                    case 'role_name': {
                      const role_name = dp.originalValue as string;
                      const role = getRoleTranslation(role_name) ?? role_name;

                      return {
                        ...dp,
                        label: t('responsible'),
                        value: role,
                      };
                    }
                    default:
                      return dp;
                  }
                });
            }
            case AuditType.AbsenceShort:
            case AuditType.AbsenceLong: {
              const keyLabelMap = {
                startDate: t('laStartDate'),
                endDate: t('laEndDate'),
                reasonFK: t('laAbsenceReason'),
                statusFK: t('laStatus'),
                days: t('laDays'),
                info: t('laInfo'),
                balanceYear: t('laYear'),
                absenceHours: t('laHours'),
                plannedWorkingHours: t('laPlannedWorkingHours'),
              };

              return getDefaultDataPoints(data)
                .filter(filterDataPointByAllowedKeys(Object.keys(keyLabelMap)))
                .map(getTranslatedDataPointLabel(keyLabelMap))
                .map((dp): UserActivityLogEventDataPoint => {
                  const getDate = (v: unknown) =>
                    getFormattedDateString(convertToDateOrUndefined(v), 'L');

                  switch (dp.key) {
                    case 'startDate': {
                      return {
                        ...dp,
                        value: getDate(dp.originalValue),
                      };
                    }
                    case 'endDate': {
                      return {
                        ...dp,
                        value: getDate(dp.originalValue),
                      };
                    }
                    case 'reasonFK': {
                      return {
                        ...dp,
                        value: getEnumName(
                          auditType === AuditType.AbsenceLong
                            ? absenceEnums.longReason
                            : absenceEnums.shortReason,
                          dp.value as string,
                        ),
                      };
                    }
                    case 'statusFK': {
                      return {
                        ...dp,
                        value: getEnumName(
                          absenceEnums.status,
                          dp.value as string,
                        ),
                      };
                    }
                    default:
                      return dp;
                  }
                });
            }
            case AuditType.PersonProfile: {
              const keyLabelMap = {
                ssn: t('fSsnInputLabel'),
                gender: t('laGender'),
                first_name: t('laFirstName'),
                last_name: t('laLastName'),
                given_names: t('laGivenNames'),
                date_of_birth: t('laDoB'),
                nationality_fk: t('laNationality'),
                employee_number: t('laEmpNumber'),
              };

              return getDefaultDataPoints(data)
                .filter(
                  filterDataPointByDeniedKeys([
                    'picture_id',
                    '_nationality_grp_fk',
                  ]),
                )
                .map(getTranslatedDataPointLabel(keyLabelMap))
                .map((dp) => {
                  switch (dp.key) {
                    case 'gender': {
                      const gender = dp.originalValue as string;

                      const genderTranslation: { [p: string]: string } = {
                        M: t('laMale'),
                        F: t('laFemale'),
                        O: t('laOther'),
                      };

                      return {
                        ...dp,
                        value: genderTranslation[gender] ?? '',
                      };
                    }
                    case 'nationality_fk': {
                      const nationality_fk = dp.originalValue as string;

                      return {
                        ...dp,
                        value:
                          getEnumName(countryEnums, nationality_fk) ||
                          nationality_fk,
                      };
                    }
                    default:
                      return dp;
                  }
                });
            }
            case AuditType.EmployeeHomeAddress:
            case AuditType.EmployeeOtherAddress: {
              const keyMapping = {
                po_box: t('fComPOBox'),
                address_1: t('fEmpStreetAddress1'),
                address_2: t('fEmpStreetAddress2'),
                postal_code: t('fEmpPostalCode'),
                city: t('fEmpCity'),
                region: t('fEmpState'),
                country_fk: t('fEmpCountry'),
              };
              return getDataPointsByKeyLabelMap(keyMapping).map((dp) => {
                switch (dp.key) {
                  case 'country_fk': {
                    const country_fk = dp.originalValue as string;

                    return {
                      ...dp,
                      value:
                        getEnumName(countryEnums, country_fk) || country_fk,
                    };
                  }
                  default:
                    return dp;
                }
              });
            }
            case AuditType.EmployeeWorkContact: {
              return getDataPointsByKeyLabelMap({
                office_fk: t('fEmpOfficeLoc'),
                office_floor: t('fEmpOfficeFloor'),
                office_room: t('fEmpOfficeRoom'),
              }).map((dp) => {
                switch (dp.key) {
                  case 'office_fk':
                    return {
                      ...dp,
                      value:
                        (typeof dp.value === 'number'
                          ? getOfficeById(dp.value)?.fOfficeCity
                          : undefined) ?? '',
                    };
                  default:
                    return dp;
                }
              });
            }
            case AuditType.EmployeeHomeContact:
            case AuditType.EmployeeOtherContact: {
              return [];
            }
            case AuditType.EmployeeHomeEmail:
            case AuditType.EmployeeWorkEmail:
            case AuditType.EmployeeOtherEmail: {
              return getDataPointsByKeyLabelMap({
                email_address: t('fEmpEmailHome'),
              });
            }
            case AuditType.EmployeeHomePhone:
            case AuditType.EmployeeWorkPhone:
            case AuditType.EmployeeOtherPhone: {
              return getDataPointsByKeyLabelMap({
                phone_number: t('fEmpPhoneWork'),
              });
            }
            case AuditType.EmployeeWorkMobile:
              return getDataPointsByKeyLabelMap({
                phone_number: t('laWorkMobile'),
              }).map((dp): UserActivityLogEventDataPoint => {
                if (dp.key === 'phone_number') {
                  // Rename property key to prevent conflict with EmployeeWorkPhone,
                  // when employee Work Contact events are compacted to single row.
                  return {
                    ...dp,
                    key: 'mobile_number',
                  };
                }
                return dp;
              });
            default: {
              return getDefaultDataPoints(data);
            }
          }
        }
        default: {
          return getDefaultDataPoints(data);
        }
      }
    },
    [
      t,
      getRoleTranslation,
      getUnitByIdIncludeDeleted,
      getLanguageByCode,
      enumsData,
      getOfficeById,
    ],
  );

  return {
    getTranslatedEventData,
  };
};
